import { booleanAttribute, ChangeDetectionStrategy, Component, computed, input, OnDestroy, OnInit, output } from '@angular/core';
import { MatOption } from '@angular/material/core';

import { AttributeDirective } from 'src/app/infrastructure/directives/attribute.directive';
import { SharedModule } from '../../../shared.module';
import { TOptionSortComparator } from '../../models/form-field-options';
import { _MatFormFieldComponent } from '../../components/_mat-form-field.component';
import { autocomplete, IAutoComplete } from '../../models/auto-complete.model';

@Component({
    selector: 'app-select-control',
    templateUrl: './select-control.component.html',
    styleUrls: [
        '../../control.scss',
        './select-control.component.scss',
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        SharedModule,
        AttributeDirective,
    ],
})
export class SelectControlComponent
    extends _MatFormFieldComponent
    implements OnInit, OnDestroy
{
    // mat-select:
    input_ariaLabel$$                       = input<string>('',                                          { alias: 'ariaLabel' });
    input_ariaLabelledBy$$                  = input<string>('',                                          { alias: 'ariaLabelledBy' });
    input_canSelectNullableOptions$$        = input(false,                                               { transform: booleanAttribute, alias: 'canSelectNullableOptions' });
    // Zonder eigen vergelijking: objecten op fieldName_value, strings op gelijkheid.
    input_compareWith$$                     = input<((o1: any, o2: any) => boolean) | null>(null,        { alias: 'compareWith' });
    input_disableOptionCentering$$          = input(false,                                               { transform: booleanAttribute, alias: 'disableOptionCentering' });
    input_disableRipple$$                   = input<boolean>(false,                                      { alias: 'disableRipple' });
    input_fieldName_display$$               = input<string>(autocomplete.itemText,                       { alias: 'fieldName_display' });
    input_fieldName_value$$                 = input<string>(autocomplete.itemId,                         { alias: 'fieldName_value' });
    input_hideSingleSelectionIndicator$$    = input<boolean>(true,                                       { alias: 'hideSingleSelectionIndicator' });
    input_list$$                            = input<IAutoComplete[] | string[]>([],                      { alias: 'list' });
    input_multiple$$                        = input<boolean>(false,                                      { alias: 'multiple' });
    input_panelClass$$                      = input<string | string[] | Set<string> | { [key: string]: any }>('', { alias: 'panelClass' });
    input_panelWidth$$                      = input<string | number | null>('auto',                      { alias: 'panelWidth' });
    input_sortComparator$$                  = input<TOptionSortComparator | undefined>(undefined,        { alias: 'sortComparator' });
    input_typeAheadDebounceInterval$$       = input<number>(0,                                           { alias: 'typeAheadDebounceInterval' });

    out_openedChange = output<boolean>({alias: 'openedChange'});
    out_selectionChange = output<any>({alias: 'selectionChange'});
    out_valueChange = output<any>({alias: 'valueChange'});

    override attributes$$        = computed(() => ({
        ...this._input_attributes$$(),
        'autofocus': this._input_autofocus$$().toString(),
    }));
    override autofocus$$         = this._input_autofocus$$;
    override control$$           = this._input_control$$;
    override controlName$$       = this._input_controlName$$;
    override disabled$$          = this._input_disabled$$;
    override errorShow$$         = this._input_errorShow$$;
    override errorStateMatcher$$ = this._input_errorStateMatcher$$;
    override id$$                = this._input_id$$;
    override lockOnLoad$$        = this._input_lockOnLoad$$;
    override placeholder$$       = this._input_placeholder$$;
    override readonly$$          = this._input_readonly$$;
    override subscriptHide$$     = this._input_subscriptHide$$;
    override tabIndex$$          = this._tabIndex$$;
    override value$$             = this._input_value$$;
    override valueReset$$        = this._input_valueReset$$;

    // mat-form-field:
    override appearance$$         = this._input_appearance$$;
    override classHeight$$        = this._input_classHeight$$;
    override color$$              = this._input_color$$;
    override floatLabel$$         = this._input_floatLabel$$;
    override hideLineRipple$$     = this._input_hideLineRipple$$;
    override hideRequiredMarker$$ = this._hideRequiredMarker$$;
    override hint$$               = this._input_hint$$;
    override hintAlign$$          = this._input_hintAlign$$;
    override hintLabel$$          = this._input_hintLabel$$;
    override label$$              = this._input_label$$;
    override required$$           = this._input_required$$;
    override subscriptSizing$$    = this._input_subscriptSizing$$;

    // Lege veldnaam (bv. vanuit een wrapper zonder waarde) valt terug op de IAutoComplete-velden.
    fieldNameDisplay$$ = computed(() => this.input_fieldName_display$$() || autocomplete.itemText);
    fieldNameValue$$ = computed(() => this.input_fieldName_value$$() || autocomplete.itemId);

    compareWith$$ = computed<(o1: any, o2: any) => boolean>(() => {
        const _compareWith = this.input_compareWith$$();
        if (_compareWith) return _compareWith;

        const _fieldName = this.fieldNameValue$$();
        return (o1: any, o2: any) =>
            (o1 !== null && typeof o1 === 'object') && (o2 !== null && typeof o2 === 'object')
                ? o1[_fieldName] === o2[_fieldName]
                : o1 === o2;
    });

    isObjectList$$ = computed<boolean>(() => {
        const _list = this.input_list$$();
        return Array.isArray(_list) && _list.every(item => typeof item === 'object');
    });

    // Material sorteert op MatOption; de eigen API sorteert op de optie-waarden.
    sortComparator$$ = computed<((a: MatOption, b: MatOption, options: MatOption[]) => number) | undefined>(() => {
        const _comparator = this.input_sortComparator$$();
        return _comparator ? (a: MatOption, b: MatOption) => _comparator(a.value, b.value) : undefined;
    });

    override ngOnInit(): void {
        super._ngOnInit();
    }

    emit_openedChange(value: boolean) {
        this.out_openedChange.emit(value);
    }

    emit_selectionChange(value: any) {
        this.out_selectionChange.emit(value);
    }

    emit_valueChange(value: any) {
        this.out_valueChange.emit(value);
    }

    itemText(item: any): string {
        return this.isObjectList$$() ? item?.[this.fieldNameDisplay$$()] : item;
    }

    trackValue(item: any): string {
        return typeof item === 'object' ? item?.[this.fieldNameValue$$()] : item;
    }

    override ngOnDestroy(): void {
        super._ngOnDestroy();
    }
}
